/**
 * API origin resolution.
 *
 * - Same-origin (default): when VITE_API_URL is unset the app calls the API at
 *   relative paths (`/api/...`). This works in prod (taitora.ai/api/* is routed
 *   to the Worker) and in local dev (the Vite proxy forwards /api → the Go API).
 * - Cross-origin (PR previews): CF Pages at *.pages.dev has no route to the dev
 *   Worker, so preview builds set VITE_API_URL to the dev Worker URL and every
 *   API/WS call is prefixed with it. Without this, /api/* falls through to the
 *   SPA and returns index.html — the "Unexpected token '<'" JSON parse error.
 */

// Trailing slashes stripped so `${API_BASE}${path}` never double-slashes.
function stripTrailingSlashes(s: string): string {
  let end = s.length;
  while (end > 0 && s[end - 1] === "/") end -= 1;
  return s.slice(0, end);
}

export const API_BASE = stripTrailingSlashes(
  (import.meta.env.VITE_API_URL as string | undefined) ?? "",
);

/** Absolute (or same-origin relative) URL for a REST/SSE path like "/api/projects". */
export function apiUrl(path: string): string {
  return `${API_BASE}${path}`;
}

/**
 * Absolute URL of the MCP endpoint, for display in setup instructions.
 *
 * Streamable HTTP is a single endpoint — /mcp, not the old /mcp/sse plus a
 * separate /mcp/message. The server dispatches on HTTP method, so POST (calls),
 * GET (server push) and DELETE all target this one URL.
 *
 * Unlike apiUrl() this must always be absolute — the user pastes it into an
 * external client (Claude Code, Cursor, opencode), which has no notion of our
 * page origin. VITE_MCP_URL overrides it outright for environments where MCP
 * is served from somewhere other than the API host.
 *
 * The same-origin fallback relies on the taitora.ai/mcp/* Worker route in
 * wrangler.toml — without it, Pages' SPA fallback answers /mcp with
 * index.html and every client the snippet was pasted into breaks (see #104).
 */
export function mcpUrl(): string {
  const override = import.meta.env.VITE_MCP_URL as string | undefined;
  if (override) return stripTrailingSlashes(override);
  if (API_BASE) return `${API_BASE}/mcp`;
  return `${window.location.origin}/mcp`;
}

/** ws(s):// URL for a WebSocket path, derived from API_BASE or the current page origin. */
export function wsUrl(path: string): string {
  if (API_BASE) {
    const u = new URL(API_BASE);
    const proto = u.protocol === "https:" ? "wss:" : "ws:";
    return `${proto}//${u.host}${path}`;
  }
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}${path}`;
}
